import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import Styles from '@/assets/js/Styles';
import Container from '@/components/Container';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { Site } from '@/declarations/models/Site';
import {
  CmpExportFile,
  CmpImportHierarchy,
  CmpImportMediaResultItem,
  CmpImportResultItem,
  CmpImportStatus,
} from '@/declarations/models/ContentMigrationImport';
import {
  CmpExportParseError,
  CmpExportSummary,
  CmpHierarchySummary,
  CmpPageDetail,
  MEDIA_KINDS,
  MEDIA_KIND_LABELS,
  pageKindLabel,
  CmpLanguageOutcome,
  CmpLanguageReview,
  importablePages,
  joinLanguageNames,
  languageOutcome,
  languageReview,
  summariseCmpExport,
} from './summariseCmpExport';
import {
  ChunkedImportResult,
  ImportPhaseProgress,
  ImportProgress,
  importCmpExportInChunks,
} from './importCmpExportInChunks';

/**
 * Importing an export file from the Content Migration Platform into the current site.
 *
 * The file is read and summarised **in the browser first**, and nothing is uploaded
 * until the operator has seen what it contains and which site it is about to land on.
 * That ordering is the whole point of this dialog: the file itself carries no site, so
 * the only confirmation that the right content is going to the right place is the one
 * shown here.
 *
 * Both the before and the after are countable *and* inspectable. Every number comes
 * with a section that lists the rows behind it, because a count on its own cannot be
 * acted on: "1 annen fil" tells an operator nothing, while "BR20182.pdf
 * (application/pdf)" tells them whether to go ahead. The same holds afterwards - a
 * migration of several hundred pages needs a per-page record of what happened, not
 * four totals.
 */

const Row: FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Container left fullWidth sx={{ justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant='body2' sx={{ color: Styles.Colors.DARK_GREY }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 500, textAlign: 'right' }}>
      {value}
    </Typography>
  </Container>
);

/**
 * A count that can be opened to show what it is made of.
 *
 * The rows are mounted only while the section is open, which is why this is a button
 * and local state rather than an Accordion: an export file routinely holds several
 * hundred pages and as many files, and MUI's Accordion keeps its children mounted when
 * collapsed - so every list in this dialog would be in the DOM at once.
 */
const DetailSection: FC<{ title: string; count: number; children: () => React.ReactNode }> = ({
  title,
  count,
  children,
}) => {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size='small'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        sx={{ textTransform: 'none', pl: 0 }}
        startIcon={<MaterialSymbol name='chevron_right' sx={open ? { transform: 'rotate(90deg)' } : undefined} />}>
        {open ? `Skjul ${title}` : `Vis ${title}`}
      </Button>
      {open && (
        <Box
          sx={{
            maxHeight: 280,
            overflowY: 'auto',
            px: 1.5,
            py: 0.5,
            border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
            borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          }}>
          {children()}
        </Box>
      )}
    </Box>
  );
};

/** "siden" for one, "de 472 sidene" for more. The section titles read as sentences -
 *  "Vis de 472 sidene som ble opprettet" - and "de 1 sidene" does not. */
function countPhrase(count: number, one: string, many: string): string {
  return count === 1 ? one : `de ${count} ${many}`;
}

/** One line of detail: what it is on the left, what is worth knowing on the right. */
const DetailRow: FC<{ primary: React.ReactNode; secondary?: React.ReactNode; aside?: React.ReactNode }> = ({
  primary,
  secondary,
  aside,
}) => (
  <Container
    left
    fullWidth
    sx={{
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 2,
      py: 0.75,
      borderBottom: `1px solid ${Styles.Colors.LIGHT_GREY}`,
      '&:last-of-type': { borderBottom: 'none' },
    }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='body2' sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
        {primary}
      </Typography>
      {secondary && (
        <Typography variant='caption' sx={{ color: Styles.Colors.DARK_GREY, wordBreak: 'break-all' }}>
          {secondary}
        </Typography>
      )}
    </Box>
    {aside && (
      <Typography variant='caption' sx={{ color: Styles.Colors.DARK_GREY, whiteSpace: 'nowrap' }}>
        {aside}
      </Typography>
    )}
  </Container>
);

const GroupHeading: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant='caption' sx={{ display: 'block', mt: 1, fontWeight: 700, textTransform: 'uppercase' }}>
    {children}
  </Typography>
);

/** The pages the file would create, in the order they are imported. */
const PageList: FC<{ items: Array<CmpPageDetail> }> = ({ items }) => (
  <>
    {items.map((item) => (
      <DetailRow
        key={`${item.sourceObjectId}-${item.slug}`}
        primary={item.title}
        secondary={
          <>
            {pageKindLabel(item.kind)}
            {item.slug ? ` · /${item.slug}` : ''}
            {item.parentPath ? ` · under /${item.parentPath}` : ''}
            {item.sourceUrl ? ` · ${item.sourceUrl}` : ''}
          </>
        }
        aside={
          <>
            {item.resolvedMedia > 0 ? `${item.resolvedMedia} medier` : 'ingen medier'}
            {item.unresolvedMedia > 0 ? ` · ${item.unresolvedMedia} mangler` : ''}
          </>
        }
      />
    ))}
  </>
);

/**
 * Every distinct EMS file, grouped by kind.
 *
 * Grouped rather than flat because the point of the list is to answer the question the
 * counts raise - "what *is* the one file under Andre filer?" - and that question is
 * always about one group.
 */
const MediaList: FC<{ summary: CmpExportSummary }> = ({ summary }) => (
  <>
    {MEDIA_KINDS.map((kind) => {
      const files = summary.media.files.filter((file) => file.kind === kind);
      if (files.length === 0) return null;
      return (
        <Box key={kind}>
          <GroupHeading>
            {MEDIA_KIND_LABELS[kind]} ({files.length})
          </GroupHeading>
          {files.map((file) => (
            <DetailRow
              key={file.dmsId}
              primary={file.title ? `${file.filename} – ${file.title}` : file.filename}
              secondary={`${file.mimetype || 'ukjent filtype'} · ${file.dmsId}`}
              // Says "brukes ikke" rather than leaving it blank: a file no page uses is
              // exactly the kind this import exists to bring in, so it is named as
              // such instead of looking like missing information.
              aside={
                file.references === 0
                  ? 'brukes ikke av noen side'
                  : `brukt på ${file.references} ${file.references === 1 ? 'side' : 'sider'}`
              }
            />
          ))}
        </Box>
      );
    })}
  </>
);

/** What the chosen file contains, before a single byte is uploaded. */
/** The languages this site has versions of - what the import can file pages under. */
function siteLocalesOf(site: Site): Array<string> {
  return (site.site_contents ?? []).map((language) => language.locale).filter(Boolean);
}

function pagesPhrase(count: number): string {
  return `${count} side${count === 1 ? '' : 'r'}`;
}

const SupportedMark: FC<{ supported: boolean }> = ({ supported }) => (
  <MaterialSymbol
    name={supported ? 'check_circle' : 'warning'}
    fill
    sx={{ fontSize: 20, color: supported ? Styles.Colors.STRONG_GREEN : Styles.Colors.STRONG_ORANGE }}
    aria-label={supported ? 'Støttet' : 'Ikke støttet'}
  />
);

const LanguageName: FC<{ language: CmpLanguageReview }> = ({ language }) => (
  <Container left sx={{ gap: 1 }}>
    <SupportedMark supported={language.supported} />
    {language.label}
  </Container>
);

/**
 * The file's languages, one line each, with a plain verdict: supported languages are
 * imported, the others are not until the language exists on the site. Shown before
 * the upload so the operator can create the language first.
 */
const LanguageReviewList: FC<{ review: Array<CmpLanguageReview> }> = ({ review }) => {
  const unsupported = review.filter((language) => !language.supported);
  const unsupportedPages = unsupported.reduce((sum, language) => sum + language.count, 0);
  return (
    <Box sx={{ mt: 2 }} data-testid='language-review'>
      <Typography sx={{ fontWeight: 500, mb: 1 }}>Språk i filen</Typography>
      {unsupported.length > 0 && (
        <Alert severity='warning' sx={{ mb: 1 }} data-testid='missing-languages-warning'>
          <AlertTitle sx={{ mb: 0.5 }}>
            {unsupported.length === 1
              ? `${unsupported[0].label} er ikke opprettet på denne siten`
              : `${unsupported.length} av språkene er ikke opprettet på denne siten`}
          </AlertTitle>
          Sidene på {joinLanguageNames(unsupported.map((language) => language.label))} ({pagesPhrase(unsupportedPages)})
          blir ikke importert. Opprett {unsupported.length === 1 ? 'språket' : 'språkene'} under språkinnstillinger
          først, og importer filen på nytt for å få dem med.
        </Alert>
      )}
      {review.map((language) => (
        <DetailRow
          key={language.locale}
          primary={<LanguageName language={language} />}
          secondary={
            language.supported
              ? `${pagesPhrase(language.count)} – importeres`
              : `${pagesPhrase(language.count)} – ikke opprettet på siten, importeres ikke`
          }
        />
      ))}
    </Box>
  );
};

/** The same list after the run: what was imported per language, and what was not. */
const LanguageOutcomeList: FC<{ outcome: Array<CmpLanguageOutcome> }> = ({ outcome }) => {
  const imported = outcome.filter((language) => language.supported);
  const notImported = outcome.filter((language) => !language.supported);
  return (
    <Box sx={{ mt: 2 }} data-testid='language-outcome'>
      <Typography sx={{ fontWeight: 500, mb: 1 }}>Språk</Typography>
      {imported.length > 0 && <GroupHeading>Importert</GroupHeading>}
      {imported.map((language) => (
        <DetailRow
          key={language.locale}
          primary={<LanguageName language={language} />}
          secondary={
            language.notImported > 0
              ? `${language.notImported} av ${pagesPhrase(
                  language.count,
                )} ble ikke importert – se listen over avviste sider`
              : undefined
          }
          aside={
            language.notImported > 0
              ? `${language.imported} av ${pagesPhrase(language.count)}`
              : pagesPhrase(language.count)
          }
        />
      ))}
      {notImported.length > 0 && <GroupHeading>Ikke importert</GroupHeading>}
      {notImported.map((language) => (
        <DetailRow
          key={language.locale}
          primary={<LanguageName language={language} />}
          secondary='Språket er ikke opprettet på siten. Opprett det under språkinnstillinger og importer filen på nytt.'
          aside={pagesPhrase(language.count)}
        />
      ))}
    </Box>
  );
};

export const HIERARCHY_LABELS: Record<CmpImportHierarchy, string> = {
  keep: 'Behold strukturen fra kilden',
  flat: 'Importer flatt',
};

/**
 * Keep the source's parent/child structure, or put every page at root.
 *
 * Only shown when the file actually has structure to keep: a flat file gives the
 * operator nothing to decide, and a choice with one sensible answer is noise. The
 * default is to keep it, which is what the import has always done - so a file imported
 * without touching this lands exactly as before.
 */
const HierarchyChoice: FC<{
  hierarchy: CmpHierarchySummary;
  value: CmpImportHierarchy;
  onChange: (value: CmpImportHierarchy) => void;
}> = ({ hierarchy, value, onChange }) => {
  if (hierarchy.nested === 0) return null;
  const missing = hierarchy.parentsMissing.length;
  return (
    <FormControl component='fieldset' sx={{ mt: 2, display: 'block' }} data-testid='hierarchy-choice'>
      <FormLabel component='legend' sx={{ fontWeight: 500, color: 'inherit', mb: 0.5 }}>
        Sidestruktur
      </FormLabel>
      <Typography variant='body2' sx={{ color: Styles.Colors.DARK_GREY, mb: 1 }}>
        {pagesPhrase(hierarchy.nested)} i filen ligger under{' '}
        {hierarchy.parents === 1 ? 'én annen side' : `${hierarchy.parents} andre sider`}
        {hierarchy.maxDepth > 2 ? `, i inntil ${hierarchy.maxDepth} nivåer` : ''}.
      </Typography>
      <RadioGroup
        name='hierarchy'
        value={value}
        onChange={(event) => onChange(event.target.value as CmpImportHierarchy)}>
        <FormControlLabel
          value='keep'
          control={<Radio color='secondary' />}
          label={
            <>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {HIERARCHY_LABELS.keep}
              </Typography>
              <Typography variant='caption' sx={{ color: Styles.Colors.DARK_GREY }}>
                Undersider legges under den overordnede siden, slik de ligger på kildenettstedet.
              </Typography>
            </>
          }
          sx={{ alignItems: 'flex-start', mb: 1 }}
        />
        <FormControlLabel
          value='flat'
          control={<Radio color='secondary' />}
          label={
            <>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {HIERARCHY_LABELS.flat}
              </Typography>
              <Typography variant='caption' sx={{ color: Styles.Colors.DARK_GREY }}>
                Alle sidene legges på rotnivå, og du bygger strukturen selv etterpå.
              </Typography>
            </>
          }
          sx={{ alignItems: 'flex-start' }}
        />
      </RadioGroup>
      {value === 'keep' && missing > 0 && (
        <Alert severity='warning' sx={{ mt: 1 }} data-testid='missing-parents-warning'>
          {missing === 1 ? 'Én av de overordnede sidene' : `${missing} av de overordnede sidene`} er ikke med i filen (
          {hierarchy.parentsMissing.map((parent) => `/${parent}`).join(', ')}). Undersidene legges under en side med
          samme sti hvis den finnes på siten fra før, ellers på rotnivå. Museum24 oppretter aldri en overordnet side på
          egen hånd.
        </Alert>
      )}
    </FormControl>
  );
};

const ExportReview: FC<{
  file: File | null;
  summary: CmpExportSummary;
  siteLocales: Array<string>;
  hierarchy: CmpImportHierarchy;
  onHierarchyChange: (value: CmpImportHierarchy) => void;
}> = ({ file, summary, siteLocales, hierarchy, onHierarchyChange }) => {
  const { media, metadata } = summary;
  const review = languageReview(summary.languages, siteLocales);
  return (
    <>
      {summary.problems.map((problem) => (
        <Alert severity='error' sx={{ mb: 2 }} key={problem}>
          {problem}
        </Alert>
      ))}
      {summary.warnings.map((warning) => (
        <Alert severity='warning' sx={{ mb: 2 }} key={warning}>
          {warning}
        </Alert>
      ))}

      <Typography sx={{ fontWeight: 500, mb: 1 }}>Filen</Typography>
      {file && <Row label='Filnavn' value={file.name} />}
      {metadata.source_instance && <Row label='Kilde' value={metadata.source_instance} />}
      {metadata.created_at && <Row label='Eksportert' value={new Date(metadata.created_at).toLocaleString('nb-NO')} />}
      {/* Which EMS the image ids come from. Shown because it is the one
       * environment-bearing fact the file carries, and the only way to notice a beta
       * file about to go into production. */}
      {metadata.ems_target && (
        <Row
          label='Bilde-id-er fra EMS'
          value={`${metadata.ems_target}${summary.dmsEnvironment ? ` (${summary.dmsEnvironment})` : ''}`}
        />
      )}

      <Divider sx={{ my: 2 }} />

      <Typography sx={{ fontWeight: 500, mb: 1 }}>Dette blir importert</Typography>
      <Row label='Sider' value={summary.pages} />
      {importablePages(review) !== summary.pages && (
        <Row label='– av disse i et språk siten har' value={importablePages(review)} />
      )}
      {summary.pagesByKind.map(({ kind, count }) => (
        <Row key={kind || 'ukjent'} label={`– ${pageKindLabel(kind, count).toLowerCase()}`} value={count} />
      ))}
      <DetailSection title={countPhrase(summary.pages, 'siden i filen', 'sidene i filen')} count={summary.pages}>
        {() => <PageList items={summary.items} />}
      </DetailSection>
      <LanguageReviewList review={review} />
      <HierarchyChoice hierarchy={summary.hierarchy} value={hierarchy} onChange={onHierarchyChange} />

      {/* The site's whole EMS library, not the pages' references: the files no page
       * uses are the ones Museum24 never had rows for, so they are counted here and
       * said out loud below. */}
      <Row label='Mediefiler' value={media.uniqueFiles} />
      {media.images > 0 && <Row label='– bilder' value={media.images} />}
      {media.videos > 0 && <Row label='– videoer' value={media.videos} />}
      {media.audio > 0 && <Row label='– lyd' value={media.audio} />}
      {media.otherFiles > 0 && <Row label='– andre filer' value={media.otherFiles} />}
      {media.unused > 0 && (
        <>
          <Row label='Brukes av sidene i filen' value={media.used} />
          <Row label='Brukes ikke av noen side' value={media.unused} />
        </>
      )}
      <DetailSection
        title={countPhrase(media.uniqueFiles, 'mediefilen i filen', 'mediefilene i filen')}
        count={media.uniqueFiles}>
        {() => <MediaList summary={summary} />}
      </DetailSection>

      {media.unresolved > 0 && (
        <>
          <Row
            label='– mangler EMS-id'
            value={<span style={{ color: Styles.Colors.ERROR }}>{media.unresolved}</span>}
          />
          <DetailSection
            title={countPhrase(
              media.unresolved,
              'mediereferansen som mangler EMS-id',
              'mediereferansene som mangler EMS-id',
            )}
            count={media.unresolved}>
            {() => (
              <>
                {media.unresolvedEntries.map((entry) => (
                  <DetailRow
                    key={entry.id}
                    primary={entry.filename}
                    secondary={`${entry.pageTitle} · ${entry.reason}`}
                  />
                ))}
              </>
            )}
          </DetailSection>
        </>
      )}

      <Alert severity='info' sx={{ mt: 2 }}>
        Sider som allerede finnes blir ikke overskrevet. Nye sider opprettes som <strong>utkast</strong> - ingenting
        publiseres.
        {media.uniqueFiles > 0 && (
          <>
            {' '}
            Alle mediefilene blir lagt i <strong>mediearkivet</strong> til denne siten, også de ingen side bruker, slik
            at redaktørene kan velge dem selv.
          </>
        )}
      </Alert>
    </>
  );
};

/** "4 min 12 s" - how long the run took, which is part of what was done. */
function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

const MEDIA_STATUS_HEADINGS: Record<CmpImportStatus, { one: string; many: string }> = {
  stored: { one: 'mediefilen som ble lagt inn', many: 'mediefilene som ble lagt inn' },
  unchanged: { one: 'mediefilen som alt fantes', many: 'mediefilene som alt fantes' },
  skipped: { one: 'mediefilen som ble hoppet over', many: 'mediefilene som ble hoppet over' },
  rejected: { one: 'mediefilen som ble avvist', many: 'mediefilene som ble avvist' },
};

const STATUS_HEADINGS: Record<CmpImportStatus, { one: string; many: string }> = {
  stored: { one: 'siden som ble opprettet', many: 'sidene som ble opprettet' },
  unchanged: { one: 'siden som alt fantes, uendret', many: 'sidene som alt fantes, uendret' },
  skipped: { one: 'siden som ble hoppet over', many: 'sidene som ble hoppet over' },
  rejected: { one: 'siden som ble avvist', many: 'sidene som ble avvist' },
};

/** What M24 actually did, per status - and per page. */
const ImportOutcome: FC<{
  result: ChunkedImportResult;
  summary: CmpExportSummary;
  elapsedMs: number;
  siteLocales: Array<string>;
  hierarchy: CmpImportHierarchy;
}> = ({ result, summary, elapsedMs, siteLocales, hierarchy }) => {
  const { summary: counts, mediaSummary: mediaCounts, failure } = result;
  const outcomeByLanguage = useMemo(
    () => languageOutcome(summary.items, result.results, siteLocales),
    [summary.items, result.results, siteLocales],
  );
  const refused = counts.rejected + counts.skipped;

  // The file knows the titles, the response knows the outcomes; neither alone can say
  // "*this page* was skipped", which is the thing an operator needs to act on.
  const titles = useMemo(
    () => new Map(summary.items.map((item) => [item.sourceObjectId, item.title])),
    [summary.items],
  );
  const titleOf = useCallback((sourceObjectId: number) => titles.get(sourceObjectId) ?? `#${sourceObjectId}`, [titles]);
  const filenames = useMemo(
    () => new Map(summary.media.files.map((mediaFile) => [mediaFile.dmsId, mediaFile.filename])),
    [summary.media.files],
  );
  const filenameOf = useCallback(
    (dmsId: string | null) => (dmsId && filenames.get(dmsId)) || dmsId || '(ukjent fil)',
    [filenames],
  );
  const mediaByStatus = useMemo(() => {
    const groups = new Map<CmpImportStatus, Array<CmpImportMediaResultItem>>();
    result.mediaResults.forEach((entry) => {
      const group = groups.get(entry.status);
      if (group) group.push(entry);
      else groups.set(entry.status, [entry]);
    });
    return groups;
  }, [result.mediaResults]);
  const byStatus = useMemo(() => {
    const groups = new Map<CmpImportStatus, Array<CmpImportResultItem>>();
    result.results.forEach((item) => {
      const group = groups.get(item.status);
      if (group) group.push(item);
      else groups.set(item.status, [item]);
    });
    return groups;
  }, [result.results]);
  // Items in the chunks that never ran. Named rather than counted, so the re-run can
  // be checked against them afterwards.
  const notAttempted = useMemo(() => {
    if (!failure) return [];
    const answered = new Set(result.results.map((item) => item.source_object_id));
    return summary.items.filter((item) => item.sourceObjectId !== undefined && !answered.has(item.sourceObjectId));
  }, [failure, result.results, summary.items]);

  return (
    <>
      {failure ? (
        <Alert severity='error' sx={{ mb: 2 }}>
          <AlertTitle>Importen stoppet underveis</AlertTitle>
          {failure.message} {counts.total} sider ble importert før det skjedde; {failure.itemsNotAttempted} ble ikke
          forsøkt. Kjør samme fil på nytt - Museum24 lar sidene som alt finnes være i fred, så det som mangler blir
          etterfylt.
        </Alert>
      ) : (
        <Alert severity={refused > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
          <AlertTitle sx={{ mb: 0 }}>
            {counts.stored} av {counts.total} sider ble opprettet
          </AlertTitle>
        </Alert>
      )}

      <Typography sx={{ fontWeight: 500, mb: 1 }}>Dette ble gjort</Typography>
      <Row label='Opprettet' value={counts.stored} />
      <Row label='Fantes allerede, uendret' value={counts.unchanged} />
      {counts.skipped > 0 && (
        <Row
          label='Hoppet over (siden finnes her alt)'
          value={<span style={{ color: Styles.Colors.ERROR }}>{counts.skipped}</span>}
        />
      )}
      {counts.rejected > 0 && (
        <Row label='Avvist' value={<span style={{ color: Styles.Colors.ERROR }}>{counts.rejected}</span>} />
      )}
      {/* Named in the record because it is the one thing about the run that cannot be
       * read off the page list afterwards. Only when there was a choice to make. */}
      {summary.hierarchy.nested > 0 && (
        <Row
          label='Sidestruktur'
          value={hierarchy === 'flat' ? 'Flat – alle sider på rotnivå' : 'Beholdt fra kilden'}
        />
      )}
      <Row label='Tid brukt' value={formatDuration(elapsedMs)} />
      <LanguageOutcomeList outcome={outcomeByLanguage} />

      {(['stored', 'unchanged', 'skipped', 'rejected'] as Array<CmpImportStatus>).map((status) => {
        const group = byStatus.get(status) ?? [];
        return (
          <DetailSection
            key={status}
            title={countPhrase(group.length, STATUS_HEADINGS[status].one, STATUS_HEADINGS[status].many)}
            count={group.length}>
            {() => (
              <>
                {group.map((item) => (
                  <DetailRow
                    key={`${status}-${item.source_object_id}`}
                    primary={
                      item.page_id ? (
                        // Opened in a new tab: the import is a run to look through, not
                        // somewhere to navigate away from.
                        <Link href={`/editor/${item.page_id}`} target='_blank' rel='noreferrer'>
                          {titleOf(item.source_object_id)}
                        </Link>
                      ) : (
                        titleOf(item.source_object_id)
                      )
                    }
                    secondary={item.error ?? item.reason}
                    aside={item.page_id ? `side ${item.page_id}` : undefined}
                  />
                ))}
              </>
            )}
          </DetailSection>
        );
      })}

      <DetailSection
        title={countPhrase(notAttempted.length, 'siden som ikke ble forsøkt', 'sidene som ikke ble forsøkt')}
        count={notAttempted.length}>
        {() => <PageList items={notAttempted} />}
      </DetailSection>

      {/* The gallery half of the run. Reported separately because it is a separate
       * thing: these files are now pickable in the media archive whether or not any
       * page uses them, which is the whole reason the import carries them. */}
      {mediaCounts.total > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 500, mb: 1 }}>Mediearkivet</Typography>
          <Row label='Lagt inn' value={mediaCounts.stored} />
          <Row label='Fantes allerede' value={mediaCounts.unchanged} />
          {mediaCounts.rejected > 0 && (
            <Row label='Avvist' value={<span style={{ color: Styles.Colors.ERROR }}>{mediaCounts.rejected}</span>} />
          )}
          {summary.media.unused > 0 && <Row label='– av disse brukt av ingen side' value={summary.media.unused} />}
          {(['stored', 'unchanged', 'rejected'] as Array<CmpImportStatus>).map((status) => {
            const group = mediaByStatus.get(status) ?? [];
            return (
              <DetailSection
                key={status}
                title={countPhrase(group.length, MEDIA_STATUS_HEADINGS[status].one, MEDIA_STATUS_HEADINGS[status].many)}
                count={group.length}>
                {() => (
                  <>
                    {group.map((entry) => (
                      <DetailRow
                        key={`${status}-${entry.dms_id}`}
                        primary={filenameOf(entry.dms_id)}
                        secondary={entry.error ?? entry.dms_id ?? undefined}
                        aside={entry.resource_id ? `mediefil ${entry.resource_id}` : undefined}
                      />
                    ))}
                  </>
                )}
              </DetailSection>
            );
          })}
        </>
      )}

      {counts.skipped > 0 && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          Noen elementer har nyere innhold i kilden enn siden her, og Museum24 overskriver ikke en side som finnes.
          Slett siden her hvis du vil importere den på nytt.
        </Alert>
      )}
    </>
  );
};

/** Real progress, not a spinner: how many of the file's pages have actually landed.
 *
 * This is the point of chunking. A spinner on a 472-page import is indistinguishable
 * from a crash, and the import takes minutes - so it says how far it has got, and the
 * count only moves once a chunk has been committed on the Museum24 side.
 */
/** One phase's bar. The media library is sent first, then the pages. */
const PhaseProgress: FC<{ label: string; unit: string; phase: ImportPhaseProgress }> = ({ label, unit, phase }) => {
  const pct = phase.total > 0 ? Math.round((phase.done / phase.total) * 100) : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant='body2' sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <LinearProgress variant='determinate' value={pct} sx={{ height: 8, borderRadius: 4 }} />
      <Container left fullWidth sx={{ justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant='body2' role='status' aria-live='polite'>
          {phase.done} av {phase.total} {unit}
        </Typography>
        <Typography variant='body2' sx={{ color: Styles.Colors.DARK_GREY }}>
          {pct} %
        </Typography>
      </Container>
    </Box>
  );
};

const ImportProgressView: FC<{ progress: ImportProgress }> = ({ progress }) => (
  <>
    <Typography sx={{ fontWeight: 500, mb: 1 }}>Importerer…</Typography>
    {progress.media.total > 0 && <PhaseProgress label='Mediearkiv' unit='mediefiler' phase={progress.media} />}
    <PhaseProgress label='Sider' unit='sider' phase={progress.items} />
    <Alert severity='info' sx={{ mt: 1 }}>
      Alt sendes i puljer og lagres underveis, så tallene over er det som faktisk er kommet inn. Mediearkivet sendes
      først, slik at sidene kan peke på filene. Ikke lukk vinduet før importen er ferdig.
    </Alert>
  </>
);

interface Props {
  open: boolean;
  site: Site;
  onClose: () => void;
}

type Stage = 'choose' | 'review' | 'running' | 'done';

const NO_PROGRESS: ImportProgress = { media: { done: 0, total: 0 }, items: { done: 0, total: 0 } };

export const ImportCmpDataModal: FC<Props> = ({ open, site, onClose }) => {
  const [stage, setStage] = useState<Stage>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<CmpExportFile | null>(null);
  const [summary, setSummary] = useState<CmpExportSummary | null>(null);
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<ChunkedImportResult | null>(null);
  const [progress, setProgress] = useState<ImportProgress>(NO_PROGRESS);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hierarchy, setHierarchy] = useState<CmpImportHierarchy>('keep');
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setStage('choose');
    setFile(null);
    setParsed(null);
    setSummary(null);
    setError('');
    setResult(null);
    setProgress(NO_PROGRESS);
    setElapsedMs(0);
    setDragging(false);
    setHierarchy('keep');
  }, []);

  const close = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const accept = useCallback(async (chosen: File) => {
    setError('');
    setFile(chosen);
    try {
      const text = await chosen.text();
      const summarised = summariseCmpExport(text);
      // Kept so the import can be sent in chunks without re-reading and re-parsing a
      // multi-megabyte file for each one.
      setParsed(JSON.parse(text) as CmpExportFile);
      setSummary(summarised);
      setStage('review');
    } catch (e) {
      // A wrong file is the ordinary mistake here, not an exceptional one: say so and
      // stay on the drop step rather than showing an empty review.
      setSummary(null);
      setParsed(null);
      setFile(null);
      setStage('choose');
      setError(e instanceof CmpExportParseError ? e.message : 'Kunne ikke lese filen.');
    }
  }, []);

  const onDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragging(false);
      const dropped = event.dataTransfer?.files?.[0];
      if (dropped) {
        await accept(dropped);
      }
    },
    [accept],
  );

  const run = useCallback(async () => {
    if (!parsed || !file || !site.id) return;
    setStage('running');
    setError('');
    setProgress({
      media: { done: 0, total: parsed.media?.length ?? 0 },
      items: { done: 0, total: parsed.items?.length ?? 0 },
    });
    const startedAt = Date.now();
    const outcome = await importCmpExportInChunks(
      site.id,
      parsed,
      file.name,
      setProgress,
      undefined,
      undefined,
      hierarchy,
    );
    setElapsedMs(Date.now() - startedAt);
    // A failed chunk still leaves everything before it imported, so the outcome is
    // shown rather than swapped for a bare error.
    setResult(outcome);
    setStage('done');
  }, [file, parsed, site.id, hierarchy]);

  const blocked = (summary?.problems.length ?? 0) > 0;

  return (
    <Dialog open={open} onClose={stage === 'running' ? undefined : close} fullWidth maxWidth='sm'>
      <DialogTitle>Importer CMP-data</DialogTitle>
      <DialogContent dividers>
        {/* Stated on every step, never only at the start: which site this lands on is
         * the one fact the file cannot tell anyone. */}
        <Alert severity='info' sx={{ mb: 2 }}>
          <AlertTitle sx={{ mb: 0 }}>
            Importeres til <strong>{site.name}</strong> (site_id {site.id})
          </AlertTitle>
          <Typography variant='body2'>
            Eksportfilen inneholder ingen site - innholdet havner på den siten du står på nå.
          </Typography>
        </Alert>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {stage === 'choose' && (
          <Box
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            sx={{
              p: 5,
              textAlign: 'center',
              cursor: 'pointer',
              borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
              border: `2px dashed ${dragging ? Styles.Colors.DARK_GREY : Styles.Colors.MEDIUM_LIGHT_GREY}`,
              backgroundColor: dragging ? Styles.Colors.LIGHT_GREY : 'transparent',
            }}>
            <Typography sx={{ fontWeight: 500 }}>Dra eksportfilen hit</Typography>
            <Typography variant='body2' sx={{ mt: 1 }}>
              eller klikk for å velge en fil (.json) fra maskinen din
            </Typography>
            <input
              ref={inputRef}
              type='file'
              accept='application/json,.json'
              hidden
              onChange={async (event) => {
                const chosen = event.target.files?.[0];
                // Cleared through the ref, so choosing the same file twice in a row
                // still fires a change event.
                if (inputRef.current) inputRef.current.value = '';
                if (chosen) {
                  await accept(chosen);
                }
              }}
            />
          </Box>
        )}

        {stage === 'review' && summary && (
          <ExportReview
            file={file}
            summary={summary}
            siteLocales={siteLocalesOf(site)}
            hierarchy={hierarchy}
            onHierarchyChange={setHierarchy}
          />
        )}

        {stage === 'running' && <ImportProgressView progress={progress} />}

        {stage === 'done' && result && summary && (
          <ImportOutcome
            result={result}
            summary={summary}
            elapsedMs={elapsedMs}
            siteLocales={siteLocalesOf(site)}
            hierarchy={hierarchy}
          />
        )}
      </DialogContent>
      <DialogActions>
        {stage === 'review' && (
          <Button onClick={reset} disabled={false}>
            Velg en annen fil
          </Button>
        )}
        <Button onClick={close} disabled={stage === 'running'}>
          {stage === 'done' ? 'Lukk' : 'Avbryt'}
        </Button>
        {(stage === 'review' || stage === 'running') && (
          <Button
            onClick={run}
            variant='contained'
            color='primary'
            disabled={stage === 'running' || blocked}
            title={blocked ? 'Filen kan ikke importeres - se feilene over.' : undefined}
            startIcon={stage === 'running' ? <CircularProgress size={16} color='inherit' /> : undefined}>
            {stage === 'running'
              ? `Importerer… ${progress.items.done} av ${progress.items.total}`
              : `Start import av ${
                  summary ? importablePages(languageReview(summary.languages, siteLocalesOf(site))) : 0
                } sider`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
